import { useState } from 'react';

import type { IntegrationCostRow, IntegrationCostBasis, IntegrationsResponse } from '../lib/types';
import { formatCount, formatCurrency } from '../lib/format';
import {
  ErrorState,
  LoadingState,
  MonthsSelect,
  PageHeader,
  Section,
  StatCard,
  TableScroll,
  useInsightsQuery,
} from '../components/ui';

/** Prefer the human name from the integrations table when labeling by-org counts. */
function vendorLabel(vendorId: string, rows: IntegrationCostRow[]): string {
  return rows.find((r) => r.id === vendorId)?.name ?? vendorId;
}

/** Human label + tone for each cost basis. */
const COST_BASIS: Record<IntegrationCostBasis, { label: string; className: string }> = {
  vendor_api: { label: 'Billed (vendor API)', className: 'bg-forest/10 text-forest' },
  flat: { label: 'Fixed plan', className: 'bg-forest/10 text-forest' },
  estimate: { label: 'Estimated', className: 'bg-orange-50 text-orange-800 ring-1 ring-inset ring-orange-200' },
  unpriced: { label: 'Usage only', className: 'bg-stone-100 text-stone-500' },
  aws: { label: 'In AWS total', className: 'bg-stone-100 text-stone-500' },
};

function CostBasisBadge({ basis }: { basis: IntegrationCostBasis }) {
  const { label, className } = COST_BASIS[basis];
  return (
    <span className={`inline-block rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide ${className}`}>
      {label}
    </span>
  );
}

function usageLabel(row: IntegrationCostRow): string {
  if (row.usageCount === null) return '—';
  const unit = row.unit ? row.unit.replace(/_/g, ' ') : 'calls';
  return `${formatCount(row.usageCount)} ${unit}`;
}

export function IntegrationsPage() {
  const [months, setMonths] = useState(6);
  const { data, isLoading, error } = useInsightsQuery<IntegrationsResponse>(
    ['integrations', months],
    `/costs/integrations?months=${months}`,
  );

  if (isLoading) return <LoadingState />;
  if (error || !data) return <ErrorState error={error} />;

  // Priced first (highest cost), then metered-but-unpriced, then the rest.
  const rows = [...data.integrations].sort((a, b) => {
    const ac = a.cost ?? -1;
    const bc = b.cost ?? -1;
    if (ac !== bc) return bc - ac;
    return (b.usageCount ?? -1) - (a.usageCount ?? -1);
  });

  const meteredCount = data.integrations.filter((i) => i.usageCount !== null && i.usageCount > 0).length;
  const unpricedMetered = data.integrations.filter(
    (i) => i.costBasis === 'unpriced' && (i.usageCount ?? 0) > 0,
  );

  return (
    <>
      <PageHeader
        title="Integrations"
        subtitle="What every third-party service costs and how much we actually use it"
        actions={<MonthsSelect value={months} onChange={setMonths} />}
      />

      <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-3">
        <StatCard
          label={`Known integration cost (${months} mo)`}
          value={formatCurrency(data.knownCostTotal)}
          hint="Fixed plans + real Twilio + estimates. Excludes AWS (see Home)."
        />
        <StatCard
          label="Integrations metered"
          value={`${meteredCount} of ${data.integrations.length}`}
          hint="Server-side calls counted per org per day"
        />
        <StatCard
          label="Metered, not yet priced"
          value={String(unpricedMetered.length)}
          hint={unpricedMetered.map((i) => i.name).join(', ') || 'none'}
        />
      </div>

      <Section
        title="Cost & usage by integration"
        subtitle={`Last ${months} months. Real billed where a vendor API exists; never an invented number.`}
      >
        <TableScroll>
          <table className="w-full min-w-[640px] whitespace-nowrap text-sm">
            <thead>
              <tr className="border-b border-stone-200 text-left text-[11px] uppercase tracking-wider text-stone-400">
                <th className="py-2 font-medium">Integration</th>
                <th className="py-2 font-medium">Capability</th>
                <th className="py-2 text-right font-medium">Usage</th>
                <th className="py-2 text-right font-medium">Cost</th>
                <th className="py-2 pl-6 font-medium">Basis</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} className="border-b border-stone-100">
                  <td className="py-2.5">
                    <div className="font-medium text-stone-800">{row.name}</div>
                    <div className="text-xs text-stone-400">{row.purpose}</div>
                  </td>
                  <td className="py-2.5 text-stone-500">{row.category}</td>
                  <td className="tabular py-2.5 text-right text-stone-600">{usageLabel(row)}</td>
                  <td className="tabular py-2.5 text-right font-medium text-stone-900">
                    {row.cost === null ? <span className="text-stone-300">—</span> : formatCurrency(row.cost)}
                  </td>
                  <td className="py-2.5 pl-6">
                    <CostBasisBadge basis={row.costBasis} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroll>
        <p className="mt-3 text-xs text-stone-400">
          <strong>Billed</strong> = real amount from the vendor's API (Twilio).{' '}
          <strong>Fixed plan</strong> = confirmed monthly charge × months.{' '}
          <strong>Estimated</strong> = metered usage × public list rate.{' '}
          <strong>Usage only</strong> = real call counts, no confirmed rate yet (free tier or unconfirmed plan).{' '}
          <strong>In AWS total</strong> = billed through AWS, counted on the Home page.
        </p>
      </Section>

      {data.byOrg.length > 0 && (
        <Section
          title="Usage by organization"
          subtitle="Which org drove which integration calls (the basis for attributing real cost)"
        >
          <TableScroll>
            <table className="w-full min-w-[480px] whitespace-nowrap text-sm">
              <thead>
                <tr className="border-b border-stone-200 text-left text-[11px] uppercase tracking-wider text-stone-400">
                  <th className="py-2 font-medium">Organization</th>
                  <th className="py-2 font-medium">Calls by integration</th>
                </tr>
              </thead>
              <tbody>
                {data.byOrg.map((org) => (
                  <tr key={org.orgId} className="border-b border-stone-100">
                    <td className="py-2.5 text-stone-700">{org.name}</td>
                    <td className="py-2.5 text-stone-500">
                      {Object.entries(org.counts)
                        .sort(([, a], [, b]) => b - a)
                        .map(([vendor, count]) => `${vendorLabel(vendor, data.integrations)} ${formatCount(count)}`)
                        .join('  ·  ')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableScroll>
        </Section>
      )}
    </>
  );
}
